import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { API_URL } from './api';

// Mutex to prevent multiple simultaneous refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

/**
 * Subscribe to token refresh completion
 * @param {Function} callback - Function to call when refresh completes
 */
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers that token refresh is complete
 * @param {{ access: string, refresh?: string }} result - Refresh response payload
 */
function onTokenRefreshed(result) {
  refreshSubscribers.forEach((callback) => callback(result, null));
  refreshSubscribers = [];
}

/**
 * Notify all subscribers that token refresh failed
 * @param {Error} error - The refresh failure
 */
function onTokenRefreshFailed(error) {
  refreshSubscribers.forEach((callback) => callback(null, error));
  refreshSubscribers = [];
}

/**
 * Check if access token needs refresh (expires in < 5 minutes)
 * @param {string} accessToken - JWT access token
 * @returns {boolean} - True if token needs refresh
 */
export function shouldRefreshToken(accessToken) {
  if (!accessToken) return false;

  try {
    const decoded = jwtDecode(accessToken);
    if (!decoded?.exp) return false;

    const currentTime = Date.now() / 1000;
    const timeUntilExpiry = decoded.exp - currentTime;
    const fiveMinutes = 5 * 60;

    // Refresh if token expires in less than 5 minutes
    return timeUntilExpiry < fiveMinutes;
  } catch (error) {
    return false;
  }
}

/**
 * Refresh access token using refresh token.
 * Returns { access, refresh } where `refresh` is only set when the backend
 * rotates refresh tokens (SimpleJWT ROTATE_REFRESH_TOKENS=True). Callers must
 * persist the rotated refresh token if present — the old one is dead.
 * @param {string} refreshToken - JWT refresh token
 * @returns {Promise<{ access: string, refresh?: string }>}
 */
export async function refreshAccessToken(refreshToken) {
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((result, error) => {
        if (error) reject(error);
        else resolve(result);
      });
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(
      `${API_URL}auth/jwt/refresh/`,
      { refresh: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    const result = {
      access: response.data.access,
      refresh: response.data.refresh
    };

    // Notify all waiting requests
    onTokenRefreshed(result);

    return result;
  } catch (error) {
    // Reject any queued waiters so they don't hang forever
    onTokenRefreshFailed(error);
    throw error;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Check if refresh token is valid and not expired
 * @param {string} refreshToken - JWT refresh token
 * @returns {boolean} - True if refresh token is valid
 */
export function isRefreshTokenValid(refreshToken) {
  if (!refreshToken) return false;

  try {
    const decoded = jwtDecode(refreshToken);
    if (!decoded?.exp) return false;

    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (error) {
    return false;
  }
}
